// Types
export type HierarchyNode<T> = T & {
  children: HierarchyNode<T>[];
};

export type HierarchyFilterResult<T> = {
  nodes: HierarchyNode<T>[];
  expandedKeys: Set<string>;
  matchKeys: Set<string>;
  matches: number;
};

export type HierarchyIndex<T> = {
  nodeByKey: Map<string, T>;
  parentByKey: Map<string, string>;
};

// Normalization
export function normalizeHierarchySearchValue(value?: string | null): string {
  return (value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function normalizeHierarchyKey(value?: string | null): string {
  return (value ?? "").trim();
}

// Indexing
export function buildHierarchyIndex<T>(
  items: T[],
  getKey: (item: T) => string,
  getParentKey: (item: T) => string
): HierarchyIndex<T> {
  const nodeByKey = new Map<string, T>();
  const parentByKey = new Map<string, string>();

  items.forEach((item) => {
    const key = getKey(item);
    if (!key) {
      return;
    }

    nodeByKey.set(key, item);

    const parentKey = getParentKey(item);
    if (parentKey) {
      parentByKey.set(key, parentKey);
    }
  });

  return { nodeByKey, parentByKey };
}

// Tree construction
export function sortHierarchyNodes<T>(
  nodes: HierarchyNode<T>[],
  getDisplayName: (item: T) => string
): HierarchyNode<T>[] {
  return [...nodes]
    .sort((left, right) => getDisplayName(left).localeCompare(getDisplayName(right)))
    .map((node) => ({
      ...node,
      children: sortHierarchyNodes(node.children, getDisplayName),
    }));
}

export function buildHierarchyTree<T>(
  items: T[],
  getKey: (item: T) => string,
  getParentKey: (item: T) => string,
  getDisplayName: (item: T) => string
): HierarchyNode<T>[] {
  const nodeByKey = new Map<string, HierarchyNode<T>>();
  const parentByKey = new Map<string, string>();
  const childKeys = new Set<string>();

  items.forEach((item) => {
    const key = getKey(item);
    if (!key) {
      return;
    }

    nodeByKey.set(key, { ...item, children: [] });

    const parentKey = getParentKey(item);
    if (parentKey) {
      parentByKey.set(key, parentKey);
    }
  });

  parentByKey.forEach((parentKey, childKey) => {
    const parentNode = nodeByKey.get(parentKey);
    const childNode = nodeByKey.get(childKey);

    if (parentNode && childNode && parentKey !== childKey) {
      parentNode.children.push(childNode);
      childKeys.add(childKey);
    }
  });

  return sortHierarchyNodes(
    Array.from(nodeByKey.entries())
      .filter(([key]) => !childKeys.has(key))
      .map(([, node]) => node),
    getDisplayName
  );
}

// Metrics
export function countHierarchyNodes<T>(nodes: HierarchyNode<T>[]): number {
  return nodes.reduce((total, node) => total + 1 + countHierarchyNodes(node.children), 0);
}

export function countHierarchyLeaves<T>(nodes: HierarchyNode<T>[]): number {
  return nodes.reduce(
    (total, node) =>
      total + (node.children.length === 0 ? 1 : countHierarchyLeaves(node.children)),
    0
  );
}

export function countHierarchyByLevel<T>(nodes: HierarchyNode<T>[]): number[] {
  const counts: number[] = [];

  const visit = (items: HierarchyNode<T>[], depth: number) => {
    items.forEach((node) => {
      counts[depth] = (counts[depth] ?? 0) + 1;
      visit(node.children, depth + 1);
    });
  };

  visit(nodes, 0);

  return counts;
}

export function collectHierarchyKeys<T>(
  nodes: HierarchyNode<T>[],
  getKey: (item: T) => string,
  getDisplayName: (item: T) => string
): string[] {
  const keys: string[] = [];

  nodes.forEach((node) => {
    const key = getKey(node) || getDisplayName(node);
    keys.push(key);
    keys.push(...collectHierarchyKeys(node.children, getKey, getDisplayName));
  });

  return keys;
}

// Path resolution
export function getHierarchyPath<T>(
  item: T | null | undefined,
  items: T[],
  getKey: (item: T) => string,
  getParentKey: (item: T) => string
): T[] {
  if (!item) {
    return [];
  }

  const index = buildHierarchyIndex(items, getKey, getParentKey);
  const currentKey = getKey(item);

  if (!currentKey) {
    return [item];
  }

  const path: T[] = [];
  const visited = new Set<string>();
  let cursorKey: string | undefined = currentKey;

  while (cursorKey && !visited.has(cursorKey)) {
    visited.add(cursorKey);
    const current = index.nodeByKey.get(cursorKey);
    if (!current) {
      break;
    }

    path.push(current);
    cursorKey = index.parentByKey.get(cursorKey);
  }

  if (path.length === 0) {
    path.push(item);
  }

  return path.reverse();
}

// Filtering
export function filterHierarchyTree<T>(
  nodes: HierarchyNode<T>[],
  searchTerm: string,
  getKey: (item: T) => string,
  getDisplayName: (item: T) => string,
  getSearchTerms: (item: T) => string[]
): HierarchyFilterResult<T> {
  if (!searchTerm) {
    return {
      nodes,
      expandedKeys: new Set<string>(),
      matchKeys: new Set<string>(),
      matches: countHierarchyNodes(nodes),
    };
  }

  const result: HierarchyFilterResult<T> = {
    nodes: [],
    expandedKeys: new Set<string>(),
    matchKeys: new Set<string>(),
    matches: 0,
  };

  nodes.forEach((node) => {
    const key = getKey(node) || getDisplayName(node);
    const childResult = filterHierarchyTree(
      node.children,
      searchTerm,
      getKey,
      getDisplayName,
      getSearchTerms
    );
    const selfMatches = getSearchTerms(node).some((value) => value.includes(searchTerm));

    if (selfMatches || childResult.nodes.length > 0) {
      result.nodes.push({
        ...node,
        children: selfMatches ? node.children : childResult.nodes,
      });
      result.matches += selfMatches ? 1 : 0;
      result.matches += childResult.matches;
      if (selfMatches) {
        result.matchKeys.add(key);
      }
      childResult.matchKeys.forEach((childKey) => result.matchKeys.add(childKey));

      if (!selfMatches && childResult.nodes.length > 0) {
        result.expandedKeys.add(key);
      }
      childResult.expandedKeys.forEach((childKey) =>
        result.expandedKeys.add(childKey)
      );
    }
  });

  return result;
}
